'use client';

import {
  Button,
  MarkdownPreview,
  Spinner,
  Text,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { File01Icon } from '@terra/ui-icons';
import { DEFAULT_COPILOT_MODE } from '@terra/shared-dto-chat-types';
import { useTranslations } from 'next-intl';
import { useChatFile } from '../../hooks/api/useChatFilesystem';
import { useExecuteCopilotPlan } from '../../hooks/useCopilotPlan';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import type { Tab } from '../../store/layoutStore';

type PlanViewerTabData = Extract<
  Tab,
  {
    type: 'plan-viewer';
  }
>;

export function PlanViewerTab({ tab }: { tab: PlanViewerTabData }) {
  const t = useTranslations('chat.copilot.plan');
  const tCard = useTranslations('designSystem.chat.copilotPlanCard');
  const { chatId, filePath } = tab.data;
  const { data: file, isLoading, isError } = useChatFile(chatId, filePath);
  const currentMode = useChatInstancesStore(
    (state) => state.configs.get(chatId)?.mode ?? DEFAULT_COPILOT_MODE,
  );
  const executePlan = useExecuteCopilotPlan(chatId);

  return (
    <VStack h="full" w="full" gap="0" alignItems="stretch" minH="0">
      <HStack
        gap="2"
        alignItems="center"
        px="4"
        py="3"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        flexShrink={0}
      >
        <Box color="fg.muted" display="flex">
          <File01Icon size={14} />
        </Box>
        <Text fontSize="xs" color="fg.muted" fontFamily="mono" truncate>
          {filePath}
        </Text>
        {currentMode !== 'agent' ? (
          <Button
            size="xs"
            variant="ghost"
            type="button"
            ml="auto"
            onClick={executePlan}
          >
            {tCard('build')}
          </Button>
        ) : null}
      </HStack>

      <Box flex="1" minH="0" overflow="auto" px="6" py="4">
        {isLoading ? (
          <HStack justifyContent="center" py="10">
            <Spinner size="sm" />
          </HStack>
        ) : isError || !file ? (
          <VStack justifyContent="center" alignItems="center" py="10" gap="1">
            <Text fontSize="sm" color="fg.muted">
              {t('empty')}
            </Text>
          </VStack>
        ) : (
          <MarkdownPreview source={file.content} />
        )}
      </Box>
    </VStack>
  );
}
